'use client';

import { useCallback, useEffect, useState } from 'react';
import { getBasePath } from '@/lib/base-url-core';
import { useAuthStore } from '@/stores/user.store';
import { useThemeStore } from '@/stores/theme.store';
import { SessionTimeout } from './SessionTimeout';
import { installBfcacheGuard, markSignedOutInThisDocument } from './bfcacheGuard';

/**
 * Decides whether this browser has a session worth watching, and only then starts the clock.
 *
 * Mounted once in `RootStoreProvider` rather than in the two layouts. Why: a signed-in
 * customer genuinely lives on `(public)` routes (`/t/{id}`, `/z/{lane}`, `/qr/…`) as well as
 * `(app)` ones, so the layout split does not map onto session surfaces; two mounts would run
 * two intervals and two activity trackers during overlapping route transitions; and
 * `AppBootGate` swaps `children` for a loader during boot, which would remount a watcher
 * placed below it and reset the clock on every boot phase.
 *
 * ## Invariants:
 * - Every gate here is cheap and hook-free of timers. Why: this is the SSR and guest firewall.
 *   `SessionTimeout` — which starts an interval and touches `localStorage` — is not rendered
 *   at all until all of them pass, so a server render or a signed-out visitor never reaches it
 *   (AC 7: a guest has no session to end).
 * - Impersonation is excluded. Why: a back-office support session carries no refresh token, is
 *   already per-tab and short-lived, and there is nothing to revoke — timing it out would only
 *   eject the support agent mid-investigation.
 */
export const SessionTimeoutWatcher = () => {
  // `mounted` keeps the first client render identical to the server's, so nothing here can
  // cause a hydration mismatch.
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    // "Has the browser taken over yet" is only knowable from an effect, and the one extra
    // render it costs happens once per page load.
    // eslint-disable-next-line react-hooks/set-state-in-effect -- standard hydration gate
    setMounted(true);
  }, []);

  useEffect(() => installBfcacheGuard(), []);

  const isAuthInitialized = useAuthStore((state) => state.isAuthInitialized);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isImpersonating = useAuthStore((state) => state.isImpersonating);
  const userId = useAuthStore((state) => state.user?.userId);
  const companyId = useThemeStore((state) => state.theme?.companyId);

  const signOut = useCallback(async () => {
    // Written before the navigation so a bfcache restore of this document can detect that the
    // session ended and force a reload rather than showing a signed-in page (AC 10).
    markSignedOutInThisDocument();
    await useAuthStore.getState().logout();
    const basePath = getBasePath(window.location.pathname);
    const loginPath = basePath === '/' ? '/login' : `${basePath}/login`;
    // A hard navigation rather than a router push: a token refresh already in flight can land
    // after logout() and repopulate the store, putting the customer straight back into a live
    // session. Replacing rather than assigning also drops the pre-logout page from history, so
    // Back cannot target it. The sign-out reason is in localStorage, so it survives this.
    //
    // No returnUrl on purpose: every user-scoped store has just been reset, so returning to a
    // half-filled checkout would resume a page whose state is gone.
    window.location.replace(loginPath);
  }, []);

  if (
    !mounted ||
    !isAuthInitialized ||
    !isAuthenticated ||
    isImpersonating ||
    !userId ||
    !companyId
  ) {
    return null;
  }

  return (
    <SessionTimeout companyId={companyId} userKey={`${companyId}::${userId}`} signOut={signOut} />
  );
};

SessionTimeoutWatcher.displayName = 'SessionTimeoutWatcher';
